import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  UserGroupIcon,
  HeartIcon,
  FlagIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import PlaceholderImage from '../components/PlaceholderImage';

const About: React.FC = () => {
  const values = [
    {
      icon: UserGroupIcon,
      title: 'people first',
      description: 'Every AI solution is built for people and driven by what matters to them.',
    },
    {
      icon: HeartIcon,
      title: 'purpose driven',
      description: 'Apply AI to enhance human potential and simplify complexities. ',
    },
    
    {
      icon: FlagIcon,
      title: 'excel where it matters',
      description: 'Invest in state-of-the-art technology and best practices.',
    },
    {
      icon: ShieldCheckIcon,
      title: 'keep it real',
      description: 'Generate value with consistency, transparency and measurable results.',
    },
  ];

  const team = [
    {
      name: 'Dr. Sarah Johnson',
      role: 'CEO & AI Research Lead',
      bio: 'PhD in Computer Science with 15+ years in AI research and business leadership.',
      image: '/images/team-ceo.jpg',
    },
    {
      name: 'Michael Chen',
      role: 'CTO & Engineering Lead',
      bio: 'Former Google AI engineer with expertise in large-scale ML systems and infrastructure.',
      image: '/images/team-cto.jpg',
    },
  ];

  
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-secondary-50 section-padding">
        <div className="container-custom">
          <div>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-6">
                About{' '}
                <span className="text-primary-500">boop logics</span>
              </h1>
              <p className="text-md text-secondary-600 mb-8">
                A team of AI experts, engineers, and business strategists 
                dedicated to helping organizations use AI to solve complex challenges and drive business growth and efficiency.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="btn-primary text-center">
                  Get in Touch
                </Link>
                <Link to="/services" className="btn-secondary text-center">
                  Our Services
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-2xl font-bold text-secondary-900 mb-6">Our Vision</h2>
              <p className="text-md text-secondary-600 mb-6">
                Empower businesses to scale rapidly with AI.
              </p>
             
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h2 className="text-2xl font-bold text-secondary-900 mb-6">Our Mission</h2>
              <p className="text-md text-secondary-600 mb-6">
                To democratize AI by making solutions 
                accessible, affordable, and impactful for small to mid-sized businesses. We believe 
                that AI should be a force for upliftment, driving innovation while maintaining 
                ethical standards and human-centric design.
              </p>
              <p className="text-md text-secondary-600">
                Our team combines deep technical expertise with business acumen to deliver 
                AI solutions that not only work technically but also drive measurable 
                business outcomes and create lasting value.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-secondary-50 section-padding">
        <div className="container-custom">
          <div className="text-center mb-2 border-b border-secondary-200 pb-4">
            <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-4">
              Our Values
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 pb-2">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                  {value.title}
                </h3>
                <p className="text-md text-secondary-600">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      {/*
      <section className="bg-white section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              Our Leadership Team
            </h2>
            <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
              Meet the experts behind our AI solutions
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-32 h-32 rounded-full overflow-hidden mx-auto mb-4">
                  <PlaceholderImage
                    alt={member.name}
                    className="w-full h-full"
                    type="team"
                  />
                </div>
                <h3 className="text-xl font-semibold text-secondary-900 mb-1">
                  {member.name}
                </h3>
                <p className="text-primary-600 font-medium mb-3">{member.role}</p>
                <p className="text-secondary-600 text-sm">
                  {member.bio}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* CTA Section */}
      <section className="bg-primary-600 section-padding">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-xl md:text-2xl font-bold text-white mb-4">
              Ready to work with us?
            </h2>
            <p className="text-lg text-primary-100 mb-8 max-w-2xl mx-auto">
              Let's discuss how our team can help transform your business with AI solutions.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="btn-primary text-center bg-white text-primary-600 hover:bg-secondary-100 transition-colors duration-200">
                Get Started
              </Link>
              <Link
                to="/services"
                className="btn-primary text-center bg-white text-primary-600 hover:bg-secondary-100 transition-colors duration-200">
                View Services
              </Link>
            </div>
  
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
